import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, SliderInput } from '@meitu/saas-ui';
import { Editor } from '@meitu/saas-editor-core/es/index';
import type { ImageEffectPlugin } from '@meitu/saas-editor-core/es/layers/image-layer';
import { ILayerProps } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { useLocaleStore } from '@/i18n/index';
import classicImg from '../assets/blurEffect.jpg';
import glassImg from '../assets/glassEffect.jpg';

enum BlurType {
    none,
    classic,
    glass,
}
const ImageBlur: React.FC<{
    setPanel: Function;
    editor: Editor;
}> = ({ setPanel, editor }) => {
    const { $t } = useLocaleStore();
    const imageEffectPlugin = useMemo(() => editor.getInstance<ImageEffectPlugin>('ImageEffectPlugin'), [editor]);

    const [originLayer, setOriginLayer] = useState<ILayerProps>();
    const originLayerRef = useRef<ILayerProps>();

    const [blurType, setBlurType] = useState(BlurType.none);

    const [alpha, setAlpha] = useState(50);

    const [activeLayer, setActiveLayer] = useState<ILayerProps | null>(editor.services.frame.getActivePickLayer(true));

    useEffect(() => {
        return () => {
            if (originLayerRef.current) {
                imageEffectPlugin.cancelBlur(originLayerRef.current);
            }
            imageEffectPlugin.resetBlurParams();
        };
    }, [imageEffectPlugin]);

    useEffect(() => {
        const eventId = editor.services.frame.onActiveLayerDataUpdate((layer, pickLayer) => {
            if (pickLayer) {
                setActiveLayer({ ...pickLayer });
            } else if (layer) {
                setActiveLayer({ ...layer });
            } else {
                setActiveLayer(null);
            }
        });

        return () => {
            editor.off(eventId);
        };
    }, [editor]);
    useEffect(() => {
        if (!activeLayer || originLayer !== undefined) {
            return;
        }

        const cleanLayer = imageEffectPlugin.getCleanBlurLayer(activeLayer);
        originLayerRef.current = cleanLayer;
        setOriginLayer(cleanLayer);
    }, [activeLayer, imageEffectPlugin, originLayer]);

    const cancelBlurClick = () => {
        if (!originLayer) {
            return;
        }

        imageEffectPlugin.cancelBlur(originLayer);
        setBlurType(BlurType.none);
        window._stat_track?.('hb_layers_func_click', {
            func_id: '2_108',
        });
    };

    const classicBlurClick = () => {
        setBlurType(BlurType.classic);
        if (!originLayer) {
            return;
        }

        void imageEffectPlugin.previewClassicBlur(originLayer, alpha).catch(console.error);
        window._stat_track?.('hb_layers_func_click', {
            func_id: '2_108',
        });
    };

    const glassBlurClick = async () => {
        setBlurType(BlurType.glass);
        if (!originLayer) {
            return;
        }
        await imageEffectPlugin.previewGlassBlur(originLayer, alpha);
        window._stat_track?.('hb_layers_func_click', {
            func_id: '2_108',
        });
    };

    const classicBlurAlphaChange = (val: number) => {
        setAlpha(val);
        void imageEffectPlugin.changeClassicBlurAlpha(val).catch(console.error);
    };

    const glassBlurAlphaChange = (val: number) => {
        setAlpha(val);
        void imageEffectPlugin.changeGlassBlurAlpha(val).catch(console.error);
    };

    const applyClick = async () => {
        if (!originLayer || !activeLayer) {
            return;
        }

        const newLayer = await imageEffectPlugin.applyBlur(
            blurType === BlurType.classic ? 'classic' : 'glass',
            activeLayer,
        );
        if (!newLayer) return;
        const cleanLayer = imageEffectPlugin.getCleanBlurLayer(newLayer);
        setOriginLayer(cleanLayer);
        originLayerRef.current = cleanLayer;
        setPanel(null);
        window._stat_track?.('hb_layers_func_click', {
            func_id: '2_108',
        });
        window._stat_track?.('hb_layers_func_use', {
            func_id: '2_108',
        });
    };

    return (
        <div className="c-image-effect-blur-content">
            <div className="effect-list">
                <div className={`item ${blurType === BlurType.none ? 'active' : ''}`} onClick={cancelBlurClick}>
                    <div className="none">
                        <IconFont name="SlashCircleBold" fontSize={28} color="rgba(171, 173, 178, 1)" />
                    </div>
                    <span className="name">{$t((v) => v.imageEffectPanel.imageBlur.cancelBlur)}</span>
                </div>

                <div
                    className={`item ${blurType === BlurType.classic ? 'active' : ''}`}
                    onClick={() => {
                        classicBlurClick();
                    }}
                >
                    <img src={`${classicImg}`} alt="图片" />
                    <span className="name">{$t((v) => v.imageEffectPanel.imageBlur.classic)}</span>
                </div>

                <div
                    className={`item ${blurType === BlurType.glass ? 'active' : ''}`}
                    onClick={() => {
                        glassBlurClick();
                    }}
                >
                    <img src={`${glassImg}`} alt="图片" />
                    <span className="name">{$t((v) => v.imageEffectPanel.imageBlur.glass)}</span>
                </div>
            </div>

            <div className={`${blurType !== BlurType.none ? '' : 'disable'} progress`}>
                <span className="text">{$t((v) => v.imageEffectPanel.imageBlur.strength)}</span>
                <SliderInput
                    min={1}
                    max={100}
                    value={alpha}
                    precision={1}
                    showStepBtn
                    type="horizontal"
                    onChange={(val: number) => {
                        if (blurType === BlurType.classic) {
                            classicBlurAlphaChange(val);
                        } else {
                            glassBlurAlphaChange(val);
                        }
                    }}
                    onAfterChange={(val: number) => {
                        if (blurType === BlurType.classic) {
                            classicBlurAlphaChange(val);
                        } else {
                            glassBlurAlphaChange(val);
                        }
                        window._stat_track?.('hb_layers_func_click', {
                            func_id: '2_108',
                        });
                    }}
                    onClick={() => {}}
                />
            </div>

            <div className="operate">
                <Button
                    type="ghost"
                    onClick={() => {
                        cancelBlurClick();
                        setPanel(null);
                    }}
                >
                    {$t((v) => v.imageEffectPanel.imageBlur.cancel)}
                </Button>
                <Button
                    className="done"
                    onClick={() => {
                        if (blurType === BlurType.none) {
                            setPanel(null);
                            return;
                        }
                        applyClick();
                    }}
                >
                    {$t((v) => v.imageEffectPanel.imageBlur.apply)}
                </Button>
            </div>
        </div>
    );
};

export default ImageBlur;
